import React, { cloneElement, isValidElement, useMemo } from 'react';

import { CodeBlockType } from '../../types';
import { getUnifiedCodeLanguage } from '../../utils/getUnifiedCodeLanguage';
import { getCodeBlockInformation } from '../utils/codeBlock';

const getInformation = (child: React.ReactNode) => {
  if (isValidElement(child) && child.props) {
    const { className, title } = child.props;
    const [lang, fallbackTitle] = getCodeBlockInformation(className);

    return [lang, title || fallbackTitle];
  }
  return ['', ''];
};

interface Return {
  codeBlocks: React.ReactNode[];
  titles: string[];
  labels: string[];
}

export const useCodeBlocks = (
  children: React.ReactNode,
  codeBlockLabels?: string[],
): Return => {
  const { codeBlocks, titles, labels } = useMemo(() => {
    const defaultState: Return = {
      codeBlocks: [],
      titles: [],
      labels: [],
    };

    return React.Children.toArray(children).reduce(
      (acc: Return, child, index) => {
        if (!isValidElement(child)) return acc;

        const childNode = React.Children.toArray(child.props.children)[0];

        const clonedChild = cloneElement(
          child,
          {},
          cloneElement(child.props.children, {
            type: CodeBlockType.Multi,
          }),
        );

        // check if the element is a code block or a title node
        const [lang, title] = getInformation(childNode);
        if (lang) {
          return {
            ...acc,
            codeBlocks: [...acc.codeBlocks, clonedChild],
            labels: [
              ...acc.labels,
              codeBlockLabels && codeBlockLabels[index]
                ? codeBlockLabels[index]
                : getUnifiedCodeLanguage(lang)?.name || '',
            ],
            titles: [...acc.titles, title],
          };
        }

        return acc;
      },
      defaultState,
    );
  }, [children, codeBlockLabels]);

  return { codeBlocks, titles, labels };
};
